import { CreateButton, Show, useModal } from "@refinedev/antd";
import { Button, Space, Tag, Input, Row, Col } from "antd";
import { IResourceComponentsProps, useShow } from "@refinedev/core";
import {
  AppstoreOutlined,
  RocketOutlined,
  BankOutlined,
  TeamOutlined,
  GlobalOutlined,
  BookOutlined,
  FileProtectOutlined,
  CompassOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import { ISubscription } from "interfaces";
import prettyBytes from "pretty-bytes";
import React from "react";
import {
  ShowField,
  ShowSection,
  ShowFieldGrid,
  ShowModal,
} from "components/show";

export const SubscriptionShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<ISubscription>();
  const record = result;

  const {
    modalProps: modalPropsVessel,
    show: showVessel,
    close: closeVessel,
  } = useModal();
  const {
    modalProps: modalPropsShipco,
    show: showShipco,
    close: closeShipco,
  } = useModal();
  const {
    modalProps: modalPropsShipcoGroup,
    show: showShipcoGroup,
    close: closeShipcoGroup,
  } = useModal();
  const {
    modalProps: modalPropsMetadataAvcs,
    show: showMetadataAvcs,
    close: closeMetadataAvcs,
  } = useModal();
  const {
    modalProps: modalPropsMetadataAdp,
    show: showMetadataAdp,
    close: closeMetadataAdp,
  } = useModal();
  const {
    modalProps: modalPropsMetadataArcs,
    show: showMetadataArcs,
    close: closeMetadataArcs,
  } = useModal();
  const {
    modalProps: modalPropsMetadataEnp,
    show: showMetadataEnp,
    close: closeMetadataEnp,
  } = useModal();

  const hasVessel = !!record?.licence?.vessel;
  const hasShipco = !!record?.licence?.shipco;
  const hasShipcoGroup = !!record?.licence?.shipcoGroup;
  const hasAvcs = !!record?.metadataAvcs;
  const hasAdp = !!record?.metadataAdp;
  const hasEnp = !!record?.metadataEnp;
  const hasArcs = !!record?.metadataArcs;

  return (
    <Show
      breadcrumb={false}
      headerProps={{
        onBack: () => window.history.back(),
        subTitle: "active subscription",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
      isLoading={isLoading}
      footerButtons={
        <Space wrap>
          <Button type="default" onClick={showVessel}>
            Associated Vessel
          </Button>
          {hasShipco && (
            <Button type="default" onClick={showShipco}>
              Associated Shipco
            </Button>
          )}
          {hasShipcoGroup && (
            <Button type="default" onClick={showShipcoGroup}>
              Associated Shipco Group
            </Button>
          )}
          {hasAvcs ? (
            <Button type="default" onClick={showMetadataAvcs}>
              AVCS Service
            </Button>
          ) : (
            <CreateButton resource="metadata-avcs">Setup AVCS</CreateButton>
          )}
          {hasAdp ? (
            <Button type="default" onClick={showMetadataAdp}>
              ADP Service
            </Button>
          ) : (
            <CreateButton resource="metadata-adp">Setup ADP</CreateButton>
          )}
          {hasEnp ? (
            <Button type="default" onClick={showMetadataEnp}>
              e-NP Service
            </Button>
          ) : (
            <CreateButton resource="metadata-enp">Setup e-NP</CreateButton>
          )}
          {hasArcs ? (
            <Button type="default" onClick={showMetadataArcs}>
              ARCS Service
            </Button>
          ) : (
            <CreateButton resource="metadata-arcs">Setup ARCS</CreateButton>
          )}
        </Space>
      }
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        {/* Subscription Overview */}
        <ShowSection
          title="Subscription Overview"
          subtitle="Primary subscription details"
          icon={<AppstoreOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="UKHO Licence" value={record?.licence?.ukhoLicenceId} type="code" copyable />
            <ShowField label="IMO Number" value={record?.licence?.vessel?.imoNumber} type="code" copyable />
            <ShowField label="Vessel Name" value={record?.licence?.vessel?.name} />
            <ShowField label="Subscription Name" value={record?.subscriptionName} />
            <ShowField label="Service Type" value={record?.serviceType} type="tag" tagColor="purple" />
            <ShowField
              label="Enrollment Date"
              value={record?.enrollmentDate ? dayjs(record.enrollmentDate).format("DD-MMM-YYYY") : undefined}
            />
          </ShowFieldGrid>
        </ShowSection>

        <Row gutter={24}>
          {/* Status Information */}
          <Col xs={24} lg={12}>
            <ShowSection
              title="Status Information"
              subtitle="Licence and activation status"
              variant="default"
            >
              <ShowFieldGrid columns={1}>
                <ShowField label="Licence Type" value={record?.licenceType} type="status" />
                <ShowField label="Activation Status" value={record?.activationStatus} type="status" />
                <ShowField label="Hold Status" value={record?.holdStatus} type="status" />
              </ShowFieldGrid>
            </ShowSection>
          </Col>

          {/* Automation Settings */}
          <Col xs={24} lg={12}>
            <ShowSection
              title="Automation Settings"
              subtitle="Ordering and permit configuration"
              variant="default"
            >
              <ShowFieldGrid columns={1}>
                <ShowField label="Order Value Threshold" value={record?.orderValueThreshold} type="currency" />
                <ShowField label="Automated Ordering" value={record?.automatedOrdering} type="status" />
                <ShowField label="Automated Permit Issuance" value={record?.automatedPermitIssuance} type="status" />
              </ShowFieldGrid>
            </ShowSection>
          </Col>
        </Row>

        {/* Associated Entities */}
        {(hasShipco || hasShipcoGroup) && (
          <ShowSection
            title="Associated Entities"
            subtitle="Linked shipping company information"
            icon={<BankOutlined />}
            variant="subtle"
          >
            <ShowFieldGrid columns={2}>
              {hasShipco && (
                <ShowField label="Shipping Company" value={record?.licence?.shipco?.registrationName} />
              )}
              {hasShipcoGroup && (
                <ShowField label="Shipping Group" value={record?.licence?.shipcoGroup?.registrationName} />
              )}
            </ShowFieldGrid>
          </ShowSection>
        )}
      </Space>

      {/* Vessel Modal */}
      <ShowModal
        title="Vessel Details"
        subtitle={record?.licence?.vessel?.name}
        icon={<RocketOutlined />}
        open={modalPropsVessel.open ?? false}
        onClose={closeVessel}
        empty={!hasVessel}
        emptyText="No vessel associated"
        width={700}
      >
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <ShowFieldGrid columns={2}>
            <ShowField label="Vessel Name" value={record?.licence?.vessel?.name} />
            <ShowField label="Vessel Type" value={record?.licence?.vessel?.type} type="tag" tagColor="blue" />
            <ShowField label="IMO Number" value={record?.licence?.vessel?.imoNumber} type="code" copyable />
            <ShowField label="Communication Email" value={record?.licence?.vessel?.email} type="email" />
            <ShowField label="Call Sign" value={record?.licence?.vessel?.callSign} type="code" />
            <ShowField label="Max Email Size" value={record?.licence?.vessel?.maxEmailSizeKb ? prettyBytes(Number(record.licence.vessel.maxEmailSizeKb)) : null} />
            <ShowField label="Max Transmission Size" value={record?.licence?.vessel?.maxTransmissionSizeMb ? prettyBytes(Number(record.licence.vessel.maxTransmissionSizeMb) * 1024 * 1024) : null} />
            <ShowField label="Status" value={record?.licence?.vessel?.holdStatus} type="status" />
          </ShowFieldGrid>
          {record?.licence?.vessel?.notes && (
            <div>
              <ShowField label="Notes" value="" />
              <Input.TextArea rows={4} readOnly value={record?.licence?.vessel?.notes} style={{ marginTop: 8 }} />
            </div>
          )}
        </Space>
      </ShowModal>

      {/* Shipco Modal */}
      <ShowModal
        title="Shipping Company"
        subtitle={record?.licence?.shipco?.registrationName}
        icon={<BankOutlined />}
        open={modalPropsShipco.open ?? false}
        onClose={closeShipco}
        empty={!hasShipco}
        emptyText="No shipping company associated"
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="Registration Name" value={record?.licence?.shipco?.registrationName} />
          <ShowField label="Registration Number" value={record?.licence?.shipco?.registrationNumber} type="code" copyable />
          <ShowField label="GST Number" value={record?.licence?.shipco?.gstNumber} type="code" copyable />
          <ShowField label="Phone" value={record?.licence?.shipco?.phone} type="phone" />
        </ShowFieldGrid>
      </ShowModal>

      {/* Shipco Group Modal */}
      <ShowModal
        title="Shipco Group"
        subtitle={record?.licence?.shipcoGroup?.registrationName}
        icon={<TeamOutlined />}
        open={modalPropsShipcoGroup.open ?? false}
        onClose={closeShipcoGroup}
        empty={!hasShipcoGroup}
        emptyText="No shipco group associated"
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="Group Name" value={record?.licence?.shipcoGroup?.registrationName} />
          <ShowField label="Registration Number" value={record?.licence?.shipcoGroup?.registrationNumber} type="code" copyable />
          <ShowField label="GST Number" value={record?.licence?.shipcoGroup?.gstNumber} type="code" copyable />
          <ShowField label="Phone" value={record?.licence?.shipcoGroup?.phone} type="phone" />
          <ShowField label="Shipco Name" value={record?.licence?.shipco?.registrationName} />
          <ShowField label="Shipco Number" value={record?.licence?.shipco?.registrationNumber} type="code" />
        </ShowFieldGrid>
      </ShowModal>

      {/* AVCS Metadata Modal */}
      <ShowModal
        title="AVCS Service Details"
        subtitle={record?.licence?.vessel?.name}
        icon={<GlobalOutlined />}
        open={modalPropsMetadataAvcs.open ?? false}
        onClose={closeMetadataAvcs}
        empty={!hasAvcs}
        emptyText="No AVCS service configured"
        width={750}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <ShowFieldGrid columns={2}>
            <ShowField label="UKHO Licence" value={record?.licence?.ukhoLicenceId} type="code" copyable />
            <ShowField label="IMO Number" value={record?.licence?.vessel?.imoNumber} type="code" />
            <ShowField label="Vessel Name" value={record?.licence?.vessel?.name} />
            <ShowField label="Product Name" value={record?.metadataAvcs?.productName} type="tag" tagColor="blue" />
            <ShowField label="Product Group" value={record?.metadataAvcs?.productGroup} />
            <ShowField label="Subscription Type" value={record?.metadataAvcs?.subscriptionType} />
            <ShowField label="Payment Frequency" value={record?.metadataAvcs?.paymentFrequency} />
            <ShowField label="Discount Percentage" value={record?.metadataAvcs?.discountPercentage} type="percentage" />
            <ShowField label="Annual Flat Fee Per Vessel" value={record?.metadataAvcs?.annualFlatFeePerVessel} type="currency" currency={record?.metadataAvcs?.currency} />
            <ShowField label="Activation Status" value={record?.metadataAvcs?.activationStatus} type="status" />
            <ShowField label="Licence Type" value={record?.metadataAvcs?.licenceType} type="status" />
            <ShowField label="Expiry Date" value={record?.metadataAvcs?.expiryDate} type="date" />
          </ShowFieldGrid>

          <ShowSection title="Permit Configuration" variant="subtle" padding="compact">
            <ShowFieldGrid columns={2}>
              <ShowField label="Master Permit" value={record?.metadataAvcs?.masterPermit} type="code" copyable />
              <ShowField label="Master Version" value={record?.metadataAvcs?.masterPermitVersion} />
              <ShowField label="Backup Permit" value={record?.metadataAvcs?.backupPermit} type="code" copyable />
              <ShowField label="Backup Version" value={record?.metadataAvcs?.backupPermitVersion} />
              <ShowField label="Reserve 1 Permit" value={record?.metadataAvcs?.reserve1Permit} type="code" copyable />
              <ShowField label="Reserve 1 Version" value={record?.metadataAvcs?.reserve1PermitVersion} />
              <ShowField label="Reserve 2 Permit" value={record?.metadataAvcs?.reserve2Permit} type="code" copyable />
              <ShowField label="Reserve 2 Version" value={record?.metadataAvcs?.reserve2PermitVersion} />
              <ShowField label="Reserve 3 Permit" value={record?.metadataAvcs?.reserve3Permit} type="code" copyable />
              <ShowField label="Reserve 3 Version" value={record?.metadataAvcs?.reserve3PermitVersion} />
            </ShowFieldGrid>
          </ShowSection>

          <ShowFieldGrid columns={2}>
            <ShowField label="Certificate URL" value={record?.metadataAvcs?.certificateUrl} type="url" />
            <ShowField label="Schedule-A URL" value={record?.metadataAvcs?.scheduleAUrl} type="url" />
          </ShowFieldGrid>
        </Space>
      </ShowModal>

      {/* ADP Metadata Modal */}
      <ShowModal
        title="ADP Service Details"
        subtitle={record?.licence?.vessel?.name}
        icon={<BookOutlined />}
        open={modalPropsMetadataAdp.open ?? false}
        onClose={closeMetadataAdp}
        empty={!hasAdp}
        emptyText="No ADP service configured"
        width={750}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <ShowFieldGrid columns={2}>
            <ShowField label="UKHO Licence" value={record?.licence?.ukhoLicenceId} type="code" copyable />
            <ShowField label="Vessel Name" value={record?.licence?.vessel?.name} />
            <ShowField label="Product Name" value={record?.metadataAdp?.productName} type="tag" tagColor="blue" />
            <ShowField label="Product Group" value={record?.metadataAdp?.productGroup} />
            <ShowField label="ADLL Publication" value={record?.metadataAdp?.adllPublication} type="status" />
            <ShowField label="ADRS Publication" value={record?.metadataAdp?.adrsPublication} type="status" />
            <ShowField label="Total Tide Publication" value={record?.metadataAdp?.totalTidePublication} type="status" />
            <ShowField label="Currency" value={record?.metadataAdp?.currency} type="tag" tagColor="purple" />
            <ShowField label="Subscription Type" value={record?.metadataAdp?.subscriptionType} />
            <ShowField label="Payment Frequency" value={record?.metadataAdp?.paymentFrequency} />
            <ShowField label="Discount Percentage" value={record?.metadataAdp?.discountPercentage} type="percentage" />
            <ShowField label="Annual Flat Fee Per Vessel" value={record?.metadataAdp?.annualFlatFeePerVessel} type="currency" currency={record?.metadataAdp?.currency} />
            <ShowField label="Licence Type" value={record?.metadataAdp?.licenceType} type="status" />
            <ShowField label="Expiry Date" value={record?.metadataAdp?.expiryDate} type="date" />
            <ShowField label="TID" value={record?.metadataAdp?.tid} type="code" copyable />
            <ShowField label="Startup Key" value={record?.metadataAdp?.startupKey} type="code" copyable />
          </ShowFieldGrid>

          <ShowSection title="Master Machine" variant="subtle" padding="compact">
            <ShowFieldGrid columns={2}>
              <ShowField label="Machine Name" value={record?.metadataAdp?.masterMachineName} />
              <ShowField label="Machine ID" value={record?.metadataAdp?.masterMachineId} type="code" copyable />
              <ShowField label="Machine Key" value={record?.metadataAdp?.masterMachineKey} type="code" copyable />
              <ShowField label="Machine Version" value={record?.metadataAdp?.masterMachineVersion} />
            </ShowFieldGrid>
          </ShowSection>

          <ShowSection title="Backup Machine" variant="subtle" padding="compact">
            <ShowFieldGrid columns={2}>
              <ShowField label="Machine Name" value={record?.metadataAdp?.backupMachineName} />
              <ShowField label="Machine ID" value={record?.metadataAdp?.backupMachineId} type="code" copyable />
              <ShowField label="Machine Key" value={record?.metadataAdp?.backupMachineKey} type="code" copyable />
              <ShowField label="Machine Version" value={record?.metadataAdp?.backupMachineVersion} />
            </ShowFieldGrid>
          </ShowSection>
        </Space>
      </ShowModal>

      {/* e-NP Metadata Modal */}
      <ShowModal
        title="e-NP Service Details"
        subtitle={record?.licence?.vessel?.name}
        icon={<FileProtectOutlined />}
        open={modalPropsMetadataEnp.open ?? false}
        onClose={closeMetadataEnp}
        empty={!hasEnp}
        emptyText="No e-NP service configured"
        width={700}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <ShowFieldGrid columns={2}>
            <ShowField label="UKHO Licence" value={record?.licence?.ukhoLicenceId} type="code" copyable />
            <ShowField label="Vessel Name" value={record?.licence?.vessel?.name} />
            <ShowField label="Product Name" value={record?.metadataEnp?.productName} type="tag" tagColor="blue" />
            <ShowField label="Product Group" value={record?.metadataEnp?.productGroup} />
            <ShowField label="Currency" value={record?.metadataEnp?.currency} type="tag" tagColor="purple" />
            <ShowField label="Subscription Type" value={record?.metadataEnp?.subscriptionType} />
            <ShowField label="Payment Frequency" value={record?.metadataEnp?.paymentFrequency} />
            <ShowField label="Discount Percentage" value={record?.metadataEnp?.discountPercentage} type="percentage" />
            <ShowField label="Annual Flat Fee Per Vessel" value={record?.metadataEnp?.annualFlatFeePerVessel} type="currency" currency={record?.metadataEnp?.currency} />
            <ShowField label="Licence Type" value={record?.metadataEnp?.licenceType} type="status" />
            <ShowField label="Activation Status" value={record?.metadataEnp?.activationStatus} type="status" />
            <ShowField label="Expiry Date" value={record?.metadataEnp?.expiryDate} type="date" />
          </ShowFieldGrid>

          <ShowSection title="Master Configuration" variant="subtle" padding="compact">
            <ShowFieldGrid columns={2}>
              <ShowField label="User Permit" value={record?.metadataEnp?.masterUserPermit} type="code" copyable />
              <ShowField label="Machine ID" value={record?.metadataEnp?.masterMachineId} type="code" copyable />
              <ShowField label="e-Reader Version" value={record?.metadataEnp?.masterEreaderVersion} />
            </ShowFieldGrid>
          </ShowSection>

          <ShowSection title="Backup Configuration" variant="subtle" padding="compact">
            <ShowFieldGrid columns={2}>
              <ShowField label="User Permit" value={record?.metadataEnp?.backupUserPermit} type="code" copyable />
              <ShowField label="Machine ID" value={record?.metadataEnp?.backupMachineId} type="code" copyable />
              <ShowField label="e-Reader Version" value={record?.metadataEnp?.backupEreaderVersion} />
            </ShowFieldGrid>
          </ShowSection>
        </Space>
      </ShowModal>

      {/* ARCS Metadata Modal */}
      <ShowModal
        title="ARCS Service Details"
        subtitle={record?.licence?.vessel?.name}
        icon={<CompassOutlined />}
        open={modalPropsMetadataArcs.open ?? false}
        onClose={closeMetadataArcs}
        empty={!hasArcs}
        emptyText="No ARCS service configured"
        width={700}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <ShowFieldGrid columns={2}>
            <ShowField label="UKHO Licence" value={record?.licence?.ukhoLicenceId} type="code" copyable />
            <ShowField label="Vessel Name" value={record?.licence?.vessel?.name} />
            <ShowField label="Product Name" value={record?.metadataArcs?.productName} type="tag" tagColor="blue" />
            <ShowField label="Product Group" value={record?.metadataArcs?.productGroup} />
            <ShowField label="Currency" value={record?.metadataArcs?.currency} type="tag" tagColor="purple" />
            <ShowField label="Subscription Type" value={record?.metadataArcs?.subscriptionType} />
            <ShowField label="Payment Frequency" value={record?.metadataArcs?.paymentFrequency} />
            <ShowField label="Discount Percentage" value={record?.metadataArcs?.discountPercentage} type="percentage" />
            <ShowField label="Annual Flat Fee Per Vessel" value={record?.metadataArcs?.annualFlatFeePerVessel} type="currency" currency={record?.metadataArcs?.currency} />
            <ShowField label="Licence Type" value={record?.metadataArcs?.licenceType} type="status" />
            <ShowField label="Activation Status" value={record?.metadataArcs?.activationStatus} type="status" />
            <ShowField label="Expiry Date" value={record?.metadataArcs?.expiryDate} type="date" />
          </ShowFieldGrid>

          <ShowSection title="Permit Configuration" variant="subtle" padding="compact">
            <ShowFieldGrid columns={2}>
              <ShowField label="Master Permit" value={record?.metadataArcs?.masterPermit} type="code" copyable />
              <ShowField label="Master Permit Pin" value={record?.metadataArcs?.masterPermitPin} type="code" copyable />
              <ShowField label="Backup Permit" value={record?.metadataArcs?.backupPermit} type="code" copyable />
              <ShowField label="Backup Permit Pin" value={record?.metadataArcs?.backupPermitPin} type="code" copyable />
            </ShowFieldGrid>
          </ShowSection>
        </Space>
      </ShowModal>
    </Show>
  );
};
